import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";

// Ensure GSAP plugins are registered
gsap.registerPlugin(ScrollTrigger);
const faqItems = [
  {
    question: "How do I create a student account?",
    answer:
      'Students with valid GUC email addresses can create an account by clicking "Login" and selecting "Register as Student." You\'ll need your university ID and email for verification.',
  },
  {
    question: "What information do companies need to register?",
    answer:
      "Companies need to provide their business registration details, company profile, and contact information for the primary recruiter. All companies undergo verification before being approved to post internship opportunities.",
  },
  {
    question: "How are students matched with internships?",
    answer:
      "Our AI-powered matching algorithm considers a student's skills, academic performance, career interests, and personality traits to suggest relevant internships. Students can also browse and apply to any posted opportunities.",
  },
  {
    question: "How does the assessment system work?",
    answer:
      "Companies can create customized assessment criteria based on the internship requirements. They provide structured feedback at mid-term and final evaluation stages, which is shared with both the student and academic advisor.",
  },
  {
    question: "Can internships be counted for academic credit?",
    answer:
      "Yes, many internships qualify for academic credit. The platform is integrated with the GUC's academic system to track hours, tasks, and evaluations needed for credit approval. Consult your academic advisor for specific requirements.",
  },
];

export default function FAQ() {
  const sectionRef = useRef(null);
  const faqRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const faqItems = document.querySelectorAll(".faq-item");
    
    if (section && faqItems.length > 0) {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top bottom-=100",
            end: "bottom center",
            scrub: 1,
            toggleActions: "play none none reverse",
          },
        }
      );

      // FAQ items animation
      faqItems.forEach((item) => {
        gsap.fromTo(
          item,
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            scrollTrigger: {
              trigger: item,
              start: "top bottom-=100",
              end: "bottom center",
              scrub: 1,
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [faqRef, sectionRef, titleRef]);

  return (
    <section
      className="py-24 bg-primary-light"
      id="faq"
      ref={sectionRef}
      data-section="faq"
    >
      <div className="container mx-auto px-4">
        <div
          className="text-center max-w-2xl mx-auto mb-16"
          ref={titleRef}
          data-animation="fade-in"
        >
          <h2 className="text-3xl md:text-4xl font-space font-bold mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-300">
            Find answers to common questions about our platform.
          </p>
        </div>

        <div
          className="max-w-3xl mx-auto space-y-6"
          ref={faqRef}
          data-animation="accordion"
        >
          <Accordion type="single" collapsible className="space-y-6">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="faq-item glass rounded-xl overflow-hidden border-none"
              >
                <AccordionTrigger className="px-6 py-4 text-lg font-space font-medium text-left hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 text-gray-300">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
